import React, { createContext, useState, useContext, useRef, useEffect, useCallback, useMemo } from 'react';
import useSound from 'use-sound';
import { useUser } from './UserContext';
import userEnterSound from "src/assets/sounds/user_enter.mp3";
import winMoneySound from "src/assets/sounds/win_money_counter.mp3";
import revealingSound from "../assets/sounds/reka_sof.mp3";
import reloadSound from "../assets/sounds/100.mp3";
import correctSound from "../assets/sounds/correct.mp3";
import btnPlaySound from "src/assets/sounds/btn_play.mp3";
import btnStopSound from "src/assets/sounds/btn_stop.mp3";
import btnAnswerSound from "src/assets/sounds/btn_answer.mp3";
import userCorrectSound from "src/assets/sounds/user_correct.mp3";
import timerSound from "src/assets/sounds/timer.mp3";
import hundredStartSofSound from "src/assets/sounds/100_start_sof.mp3";
import hundredEndingSofSound from "src/assets/sounds/100_ending_sof.mp3";
import barPrizeSound from "src/assets/sounds/bar_prize.mp3";
import barFinishSound from "src/assets/sounds/bar_finish.mp3";

import axiosInstance from '../utils/axiosConfig';

const GAME_STATES = [
  "start",
  "paused",
  "countdown",
  "revealing",
  "guessing",
  "reload",
  "checking",
];
const GAME_STATES_CYCLE = ["start", "countdown", "revealing", "guessing", "reload", "checking", "paused"];
const COUNTDOWN_TIMER_SECONDS = 7;
const ROUNDS_BAR_STEP_ROUNDS = 10;
const ROUNDS_BAR_STEP_PERCENT = 10;
const ROUNDS_BAR_MAX_PERCENT = 100;
const ROUNDS_BAR_FREE_PLAY_PERCENT = 0;
const ROUNDS_BAR_TOPUP_TOKENS = 100;

const GameContext = createContext();

export const useGame = () => useContext(GameContext);

export const GameProvider = ({ children }) => {
  const { socket, userInfo } = useUser()
  
  // Use refs for frequently changing values to avoid re-renders
  const drawIntervalRef = useRef(null);
  const gameStateRef = useRef("start");
  const countdownSecondsRef = useRef(null);
  const timeLeftPercentRef = useRef(100);
  const piecesLeftPercentRef = useRef(100);
  const receivedPiecesRef = useRef(0);
  const totalChunksRef = useRef(1);
  const stoppedAtPieceRef = useRef(0);
  const roundSumRef = useRef(10);
  const imagePrefixRef = useRef("");
  const submittedStartToSocket = useRef(false);
  const submittedGuessToSocket = useRef(false);
  const revealingEndHandledRef = useRef(false);
  const roundsBarResetTimeoutRef = useRef(null);
  const hasShownHundredTokensRewardModalRef = useRef(false);
  const shouldPauseBeforeNextRoundRef = useRef(false);
  const pendingHundredTokensRewardRef = useRef(false);
  const pendingHundredTokensTopupRef = useRef(0);
  const pendingFirstWinCashOutRef = useRef(false);
  const hundredTokensCelebrationTimeoutRef = useRef(null);
  const guessInputRef = useRef('');
  const imageCanvasRef = useRef(null);
  
  // Batch state updates to reduce re-renders
  const [gameState, setGameState] = useState(GAME_STATES[0]);
  const [countdownSeconds, setCountdownSeconds] = useState(null);
  const [timeLeftPercent, setTimeLeftPercent] = useState(100);
  const [piecesLeftPercent, setPiecesLeftPercent] = useState(100);
  const [receivedPieces, setReceivedPieces] = useState(0);
  const [totalChunks, setTotalChunks] = useState(1);
  const [stoppedAtPiece, setStoppedAtPiece] = useState(0);
  
  // Other state variables
  const [isMuted, setmuted] = useState(false);
  const [gameInfo, setGameInfo] = useState(null);
  const [gameID, setGameID] = useState(0);
  const [users, setUsers] = useState([]);
  const [balance, setBalance] = useState(0);

  const [myWins, setMyWins] = useState(0);
  const [roundsStop, setRoundsStop] = useState([]);
  const [requiredAmount, setRequiredAmount] = useState(10);
  const [hasEnteredRound, setHasEnteredRound] = useState(false);
  const [nextRoundTime, setNextRoundTime] = useState(100);
  const [nextRoundImageFieldName, setNextRoundImageFieldName] = useState(null);
  const [timeUntilNextRound, setTimeUntilNextRound] = useState(null);
  const [guessInput, setGuessInput] = useState("");
  const [guessHint, setGuessHint] = useState(null);
  const [isVerifyingGuess, setIsVerifyingGuess] = useState(false);
  const [isCorrectAnswer, setIsCorrectAnswer] = useState(false);
  const [lastWinAmount, setLastWinAmount] = useState(null);
  const [showLowBalanceModal, setShowLowBalanceModal] = useState(false);
  const [showLastRoundLossModal, setShowLastRoundLoss] = useState(false);
  const [lastRoundLoss, setLastRoundLoss] = useState([{name:""}]);
  const [showFreePlayLimitReachedModal, setShowFreePlayLimitReachedModal] = useState(false);
  const [showHundredTokensRewardModal, setShowHundredTokensRewardModal] = useState(false);
  const [showHundredTokensBalanceCelebration, setShowHundredTokensBalanceCelebration] = useState(false);
  const [showFirstWinCashOutModal, setShowFirstWinCashOutModal] = useState(false);
  const [roundsBar, setRoundsBar] = useState({
    roundsCount: 0,
    roundsBarPercent: ROUNDS_BAR_FREE_PLAY_PERCENT,
    isPayedUser: false
  });
  // Memoized sound functions to prevent recreation
  const [playUserEnterSound] = useSound(userEnterSound, { volume: isMuted ? 0 : 1 });
  const [playRevealingSound, { stop: stopRevealingSound, sound: revealingSoundInstance }] = useSound(revealingSound, { loop: true, volume: isMuted ? 0 : 1 });
  const [playReloadSound, { stop: stopReloadSound }] = useSound(reloadSound, { loop: true, volume: isMuted ? 0 : 1 });
  const [playCorrectSound] = useSound(correctSound, { volume: isMuted ? 0 : 1 });
  const [playWinMoneySound] = useSound(winMoneySound, { volume: isMuted ? 0 : 1 });
  const [playBtnPlaySound] = useSound(btnPlaySound, { volume: isMuted ? 0 : 1 });
  const [playBtnStopSound] = useSound(btnStopSound, { volume: isMuted ? 0 : 1 });
  const [playBtnGuessingSound] = useSound(btnAnswerSound, { volume: isMuted ? 0 : 1 });
  const [playUserCorrectSound] = useSound(userCorrectSound, { volume: isMuted ? 0 : 1 });
  const [playTimerSound, { pause: pauseTimerSound, sound: timerSoundInstance }] = useSound(timerSound, {
    loop: true,
    volume: isMuted ? 0 : 1
  });

  const [playHundredStartSofSound] = useSound(hundredStartSofSound, { volume: isMuted ? 0 : 1 });
  const [playHundredEndingSofSound] = useSound(hundredEndingSofSound, { volume: isMuted ? 0 : 1 });
  const [playBarPrizeSound] = useSound(barPrizeSound, { volume: isMuted ? 0 : 1 });
  const [playBarFinishSound] = useSound(barFinishSound, { volume: isMuted ? 0 : 1 });
  // Memoized functions to prevent recreation

  const setIsMuted = useCallback((value) => {
    setmuted(value);
    localStorage.setItem('is_muted', value);
    
  }, []);


  const clearCanvas = useCallback(() => {
    if (!imageCanvasRef.current) return;
    const ctx = imageCanvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, imageCanvasRef.current.width, imageCanvasRef.current.height);
  }, []);

  const togglePlay = useCallback(() => {
    const STATE_TO_ACTION = {
      start: () => {
        socket.emit("start-game", { time: Date.now() });
        playBtnPlaySound();
        setCountdownSeconds(null);
        setGameState("countdown");
        setHasEnteredRound(true);
      },
      paused: () => {
        socket.emit("user-resume", { time: Date.now() });
        playBtnPlaySound();
        setCountdownSeconds(null);
        setGameState("countdown");
      },
      countdown: () => {
        setGameState("paused");
        socket.emit("user-pause", { time: Date.now() });
      },
      revealing: () => {
        socket.emit("stop-stream");
        playBtnStopSound();
      },
      guessing: () => { },
      checking: () => { },
    };

    if (gameState in STATE_TO_ACTION) {
      STATE_TO_ACTION[gameState]();
    }
  }, [gameState, socket, playBtnPlaySound, playBtnStopSound]);

  const checkGuess = useCallback(() => {
    playBtnGuessingSound();
    socket.emit("guess-submit", { guess: guessInputRef.current });
    setIsVerifyingGuess(true);
    setGuessHint(null);
    setGameState("checking");
  }, [socket, playBtnGuessingSound]);

  const fetchLastRoundLoss = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/auth/get-last-round-loss?userID=${userInfo.userID}&gameID=${gameID}`);
      setLastRoundLoss(response.data);
    } catch (error) {
      console.error('Error fetching last round loss:', error);
    }
  }, [userInfo.userID, gameID]);

  // Optimized canvas drawing with object pooling and batching
  const imagePool = useRef([]);
  const pendingPieces = useRef([]);
  const isDrawing = useRef(false);

  const getImageFromPool = useCallback(() => {
    if (imagePool.current.length > 0) {
      return imagePool.current.pop();
    }
    return new Image();
  }, []);

  const returnImageToPool = useCallback((image) => {
    if (imagePool.current.length < 50) { // Limit pool size
      image.src = '';
      imagePool.current.push(image);
    }
  }, []);

  const drawIncomingPieces = useCallback(() => {
    if (pendingPieces.current.length === 0 || isDrawing.current) return;
    
    isDrawing.current = true;
    const ctx = imageCanvasRef.current?.getContext("2d");
    if (!ctx) {
      isDrawing.current = false;
      return;
    }

    // Batch draw multiple pieces per frame
    const piecesToDraw = pendingPieces.current.splice(0, 10); // Draw up to 10 pieces per frame
    piecesToDraw.forEach(piece => {
      if (piece && piece.incomingImage) {
        ctx.drawImage(piece.incomingImage, piece.x, piece.y);
        returnImageToPool(piece.incomingImage);
      }
    });

    // Update received pieces count
    setReceivedPieces(prev => prev + piecesToDraw.length);
    
    isDrawing.current = false;
    
    // Continue drawing if there are more pieces
    if (pendingPieces.current.length > 0) {
      requestAnimationFrame(drawIncomingPieces);
    }
  }, [returnImageToPool]);

  // Update refs when state changes to keep them in sync
  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  useEffect(() => {
    countdownSecondsRef.current = countdownSeconds;
  }, [countdownSeconds]);

  useEffect(() => {
    timeLeftPercentRef.current = timeLeftPercent;
  }, [timeLeftPercent]);

  useEffect(() => {
    piecesLeftPercentRef.current = piecesLeftPercent;
  }, [piecesLeftPercent]);

  useEffect(() => {
    receivedPiecesRef.current = receivedPieces;
  }, [receivedPieces]);

  useEffect(() => {
    totalChunksRef.current = totalChunks;
  }, [totalChunks]);

  useEffect(() => {
    stoppedAtPieceRef.current = stoppedAtPiece;
  }, [stoppedAtPiece]);

  useEffect(() => {
    if (showLastRoundLossModal) {
      fetchLastRoundLoss();
    }
  }, [showLastRoundLossModal, fetchLastRoundLoss]);

  // Main socket effect with optimized event handling
  useEffect(() => {
    if (!imageCanvasRef.current || !socket) return;

    const clearRoundsBarResetTimeout = () => {
      if (!roundsBarResetTimeoutRef.current) {
        return;
      }

      clearTimeout(roundsBarResetTimeoutRef.current);
      roundsBarResetTimeoutRef.current = null;
    };

    if (socket.connected) {
      socket.emit('get-balance', (newBalance) => {
        setBalance(newBalance || 0);
      });
      socket.emit('get-users-state', (currentUsers) => {
        setUsers(currentUsers);
      });
    }
    
    setMyWins(userInfo.wins);
    setBalance(userInfo.creditAmount);
    function onPing(value, callback) {
      callback({ time: Date.now() });
    }

    function onCurrentBalance(value) {
      setBalance(value || 0);
    }

    function onDrawEvent(value) {
      if (!value) {
        pendingPieces.current.push(null);
        return;
      }

      const incomingImage = getImageFromPool();
      incomingImage.src = imagePrefixRef.current + value.data;

      incomingImage.onload = () => {
        pendingPieces.current.push({ x: value.x, y: value.y, incomingImage });
        if (!isDrawing.current) {
          requestAnimationFrame(drawIncomingPieces);
        }
      };
    }

    function onStreamPieces(value) {
      if (!Array.isArray(value)) return;
      
      // Clear any existing interval
      if (drawIntervalRef.current) {
        clearInterval(drawIntervalRef.current);
      }
      
      const startTime = Date.now();
      drawIntervalRef.current = setInterval(() => {
        const elapsedTime = Date.now() - startTime;
        if (elapsedTime >= 2900) {
          clearInterval(drawIntervalRef.current);
          drawIntervalRef.current = null;
          return;
        }

        // Process multiple pieces per frame
        const piecesPerFrame = Math.ceil(value.length / 43);
        for (let i = 0; i < piecesPerFrame && value.length > 0; i++) {
          const piece = value.shift();
          if (!piece) continue;

          const incomingImage = getImageFromPool();
          incomingImage.src = imagePrefixRef.current + piece.data;

          incomingImage.onload = () => {
            pendingPieces.current.push({ x: piece.x, y: piece.y, incomingImage });
            if (!isDrawing.current) {
              requestAnimationFrame(drawIncomingPieces);
            }
          };
        }
      }, 10); // ~60fps
    }

    function onChangeMetaDataEvent(value, callback) {
      setGameState(() => {
        setGuessInput('');
        setGuessHint(null);
        setReceivedPieces(0);
        setTotalChunks(value.numberOfChunks);
        imagePrefixRef.current = value.prefix;

        socket.emit('get-balance', (newBalance) => {
          setBalance(newBalance || 0);
        });

        callback();
        return 'revealing';
      });
    }

    async function onGuessStart(value) {
      if (value.stoppedAtPiece > 5000) {
        await new Promise(resolve => {
          setTimeout(resolve, 2000);
        });
      }
      
      const newTimeLeftPercent = Math.round(100 - (100 * value.stoppedAtPiece / 5400));
      setTimeLeftPercent(newTimeLeftPercent);
      setGuessHint(value.hint || null);
      setGuessInput('');
      
      setGameState(() => {
        setStoppedAtPiece(value.stoppedAtPiece);
        setCountdownSeconds(COUNTDOWN_TIMER_SECONDS);
        return 'guessing';
      });
    }

    function onGuessSuccess(value) {
      const percentRevealLeft = Number(
        value?.percentRevealLeft ?? timeLeftPercentRef.current / 100
      );
      const computedWin = Number(
        value?.win ?? (1 + percentRevealLeft) * roundSumRef.current
      );
      const wonMoreThanStake = Boolean(value?.success) && percentRevealLeft > 0;
      setLastWinAmount(wonMoreThanStake && Number.isFinite(computedWin) ? computedWin : null);

      const hasDeferredTopup = (value?.roundsBarResetDelayMs ?? 0) > 0;
      if (hasDeferredTopup) {
        pendingHundredTokensTopupRef.current = ROUNDS_BAR_TOPUP_TOKENS;
        setBalance(value.balance - ROUNDS_BAR_TOPUP_TOKENS);
      } else {
        setBalance(value.balance);
      }
      clearRoundsBarResetTimeout();
      const nextPercentFromSuccess = Math.min(
        value?.roundsBarPercent ?? ROUNDS_BAR_FREE_PLAY_PERCENT,
        ROUNDS_BAR_MAX_PERCENT
      );
      if (nextPercentFromSuccess >= ROUNDS_BAR_MAX_PERCENT) {
        pendingHundredTokensRewardRef.current = true;
        shouldPauseBeforeNextRoundRef.current = true;
      }
      setRoundsBar((prev) => {
        const nextRoundsCount = value?.roundsCount ?? prev.roundsCount;
        const nextRoundsBarPercent = Math.min(
          value?.roundsBarPercent ?? prev.roundsBarPercent,
          ROUNDS_BAR_MAX_PERCENT
        );

        if (
          nextRoundsBarPercent === prev.roundsBarPercent
        ) {
          return prev;
        }

        if (!isMuted && nextRoundsBarPercent > prev.roundsBarPercent) {
          if (
            nextRoundsBarPercent >= ROUNDS_BAR_MAX_PERCENT &&
            prev.roundsBarPercent < ROUNDS_BAR_MAX_PERCENT
          ) {
            playBarFinishSound();
          } else if (
            Math.floor(nextRoundsBarPercent / ROUNDS_BAR_STEP_PERCENT) >
            Math.floor(prev.roundsBarPercent / ROUNDS_BAR_STEP_PERCENT)
          ) {
            playBarPrizeSound();
          }
        }

        return {
          ...prev,
          roundsCount: nextRoundsCount,
          roundsBarPercent: nextRoundsBarPercent
        };
      });

      if ((value?.roundsBarResetDelayMs ?? 0) > 0) {
        roundsBarResetTimeoutRef.current = setTimeout(() => {
          if (!pendingHundredTokensRewardRef.current && !showHundredTokensRewardModal) {
            hasShownHundredTokensRewardModalRef.current = false;
          }
          setRoundsBar((prev) => ({
            ...prev,
            roundsCount: 0,
            roundsBarPercent: ROUNDS_BAR_FREE_PLAY_PERCENT
          }));
          roundsBarResetTimeoutRef.current = null;
        }, value.roundsBarResetDelayMs);
      }

      setIsCorrectAnswer(value.success || false);
      
      setTimeout(() => {
        setIsCorrectAnswer(false);
        setLastWinAmount(null);
      }, 2000);
      
      setReceivedPieces(0);
      
      if (!isMuted && value.success) {
        playWinMoneySound();
        playCorrectSound();
      }
      
      setMyWins(prev => {
        if (!value.success) return prev;
        if (prev === 0) {
          pendingFirstWinCashOutRef.current = true;
          shouldPauseBeforeNextRoundRef.current = true;
        }
        return prev + 1;
      });
      setGameState('reload');
    }

    function onCurrentUsersState(gameUsers) {
      setUsers([...gameUsers]);
    }

    function onUserAction(userActions) {
      setUsers((prevUsers) => {
        return prevUsers.map((u) => {
          if (u.userID in userActions) {
            if (!isMuted && userActions[u.userID]?.win > 0) {
              playUserCorrectSound();
            }
            return userActions[u.userID];
          }
          return { ...u };
        });
      });
    }

    function onNextRoundTime(value) {
      const currentState = gameStateRef.current;
      const imageFieldName = value?.imageFieldName || null;
      const imageCategoryName = value?.imageCategoryName || null;
      const hideCategory =
        imageCategoryName?.toLowerCase() === 'game';
      const displayCategoryName = hideCategory ? null : imageCategoryName;
      const nextRoundLabel =
        imageFieldName && displayCategoryName
          ? `${imageFieldName}/${displayCategoryName}`
          : imageFieldName || displayCategoryName || null;

      setNextRoundImageFieldName(nextRoundLabel);
      
      if (["start", "countdown", "paused"].includes(currentState)) {
        const sec = Math.max(0, Math.ceil((value?.nextRoundTime ?? 0) / 1000));
        const newCountdown = sec > 9 ? COUNTDOWN_TIMER_SECONDS : sec;
        setCountdownSeconds(newCountdown);
      }
    }

    function onLowBalance(value) {
      if (userInfo?.isPayedUser === false) {
        setShowFreePlayLimitReachedModal(true);
      } else {
        setShowLowBalanceModal(true);
      }
      setRequiredAmount(value.requiredAmount);
      setGameState('paused');
    }
    function onFreePlayLimitReached(value) {
      setShowFreePlayLimitReachedModal(true);
      setGameState('paused');
    }

    function onSetBalance(value) {
      setBalance((prev) => {
        if (
          userInfo?.isPayedUser &&
          value - prev === ROUNDS_BAR_TOPUP_TOKENS
        ) {
          pendingHundredTokensTopupRef.current = ROUNDS_BAR_TOPUP_TOKENS;
          return prev;
        }
        return value;
      });
    }

    function onRoundsBarProgress(value) {
      clearRoundsBarResetTimeout();
      const nextRoundsBarPercent = Math.min(
        value?.roundsBarPercent ?? ROUNDS_BAR_FREE_PLAY_PERCENT,
        ROUNDS_BAR_MAX_PERCENT
      );
      if (
        nextRoundsBarPercent >= ROUNDS_BAR_MAX_PERCENT &&
        !hasShownHundredTokensRewardModalRef.current
      ) {
        pendingHundredTokensRewardRef.current = true;
        shouldPauseBeforeNextRoundRef.current = true;
      }
      if (
        nextRoundsBarPercent < ROUNDS_BAR_MAX_PERCENT &&
        !pendingHundredTokensRewardRef.current &&
        !showHundredTokensRewardModal
      ) {
        hasShownHundredTokensRewardModalRef.current = false;
      }
      setRoundsBar({
        roundsCount: value?.roundsCount || 0,
        isPayedUser: Boolean(value?.isPayedUser),
        roundsBarPercent: nextRoundsBarPercent
      });
    }

    // Bind events
    const bindEvents = [
      ["connect", onConnect],
      ["disconnect", onDisconnect],
      ["session-replaced", onSessionReplaced],
      ["draw", onDrawEvent],
      ["stream-pieces", onStreamPieces],
      ["image-metadata", onChangeMetaDataEvent],
      ["ping", onPing],
      ['guess-start', onGuessStart],
      ['guess-success', onGuessSuccess],
      ['current-balance', onCurrentBalance],
      ['current-users-state', onCurrentUsersState],
      ['user-action', onUserAction],
      ['next-round-time', onNextRoundTime],
      ['low-balance', onLowBalance],
      ['free-play-limit-reached', onFreePlayLimitReached],
      ['set-balance', onSetBalance],
      ['rounds-bar-progress', onRoundsBarProgress]
    ];

    bindEvents.forEach(([event, handler]) => socket.on(event, handler));

    function onConnect() {
      console.log("CONNECTED");
      socket.emit('get-balance', (newBalance) => {
        console.log("BALANCE", newBalance);
        setBalance(newBalance || 0);
      });
    }

    function onSessionReplaced() {
      setGameState('paused');
      clearRoundsBarResetTimeout();
      if (drawIntervalRef.current) {
        clearInterval(drawIntervalRef.current);
        drawIntervalRef.current = null;
      }
      pendingPieces.current = [];
      isDrawing.current = false;
      clearCanvas();
    }

    function onDisconnect() {
      // Cleanup on disconnect
    }

    socket.on('get-users-state', (value) => {
      console.log("USERS", value);
      setGameID(value.gameID);
    });

    socket.on('gameID', (value) => {
      setGameID(value);
    });

    return () => {
      clearRoundsBarResetTimeout();
      // Cleanup all event listeners
      bindEvents.forEach(([event, handler]) => socket.off(event, handler));
      socket.off('get-users-state');
      socket.off('gameID');
      
      // Clear intervals
      if (drawIntervalRef.current) {
        clearInterval(drawIntervalRef.current);
        drawIntervalRef.current = null;
      }
      
      // Clear pending pieces
      pendingPieces.current = [];
      isDrawing.current = false;
      
      // Clear image pool
      imagePool.current = [];
    };
  }, [socket, userInfo.wins, isMuted, playWinMoneySound, playCorrectSound, playUserCorrectSound, playBarPrizeSound, playBarFinishSound, getImageFromPool, drawIncomingPieces]);

  // Optimized countdown effect
  useEffect(() => {
    if (!nextRoundTime) {
      setTimeUntilNextRound(null);
      return;
    }

    const updateCountdown = () => {
      const timeLeft = Math.max(0, Math.ceil((nextRoundTime - Date.now()) / 1000));
      setTimeUntilNextRound(timeLeft);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [nextRoundTime]);

  // Round status effect
  useEffect(() => {
    if (!socket) return;

    const handleRoundStatus = (status) => {
      if (status.canStart && (gameState === 'start' || gameState === 'paused')) {
        setNextRoundTime(status.nextRoundTime);
      }
    };

    socket.on('round-status', handleRoundStatus);

    return () => {
      socket.off('round-status', handleRoundStatus);
    };
  }, [socket, gameState]);

  useEffect(() => {
    if (!socket) return;
    if (gameState !== 'countdown') return;
    if (countdownSeconds !== null) return;

    socket.emit('get-next-round-time');
  }, [socket, gameState, countdownSeconds]);

  // Main game state effect with optimized intervals
  useEffect(() => {
    const STATE_TO_ACTION = {
      countdown: () => {
        playUserEnterSound();
        stopReloadSound();
        revealingEndHandledRef.current = false;
        
        if (stoppedAtPiece) {
          setStoppedAtPiece(() => {
            const randomAvg = Math.floor(Math.random() * (80 - 15 + 1)) + 15;
            setRoundsStop((prev) => [...prev, randomAvg]);
            return 0;
          });
        }
        
        if (!isMuted) {
          playTimerSound();
        }
        
        submittedStartToSocket.current = false;
        submittedGuessToSocket.current = false;
        setIsVerifyingGuess(false);
        setIsCorrectAnswer(false);
        setTimeLeftPercent(100);
        playHundredStartSofSound();
      },
      guessing: () => {
        playTimerSound();
        stopReloadSound();
        
        setGuessInput("");
        // guessHint is set from guess-start; keep it for the guessing UI
      },
      revealing: () => {
        revealingEndHandledRef.current = false;
        if (!isMuted) {
          
          playRevealingSound();
        }
      },
      checking: () => {
        pauseTimerSound();
      },
      reload: async () => {
        setHasEnteredRound(false);
        
        const currentTimeLeftPercent = await new Promise(resolve => {
          setTimeLeftPercent(current => {
            resolve(current);
            return current;
          });
        });
        
        playReloadSound();
        
        await new Promise(resolve => {
          const interval = setInterval(() => {
            setTimeLeftPercent(prev => {
              if (prev >= 100) {
                clearInterval(interval);
                resolve();
                return 100;
              }
              return prev + 1;
            });
          }, 2500 / (100 - currentTimeLeftPercent));
        });

        stopReloadSound();


        setIsVerifyingGuess(false);
        setTimeout(() => {
          if (shouldPauseBeforeNextRoundRef.current) {
            shouldPauseBeforeNextRoundRef.current = false;
            clearCanvas();
            setCountdownSeconds(null);
            setGameState('paused');
            if (pendingHundredTokensRewardRef.current) {
              pendingHundredTokensRewardRef.current = false;
              hasShownHundredTokensRewardModalRef.current = true;
              setShowHundredTokensRewardModal(true);
            }
            if (pendingFirstWinCashOutRef.current) {
              pendingFirstWinCashOutRef.current = false;
              setShowFirstWinCashOutModal(true);
            }
            return;
          }
          setCountdownSeconds(COUNTDOWN_TIMER_SECONDS);
          setGameState('countdown');
        }, 1200);
      }
    };

    const STATE_TO_INTERVAL = {
      countdown: [
        () => {
          console.log("CLEAR CANVAS countdown");
          clearCanvas();

          setCountdownSeconds((prev) => {
            if (prev == null) {
              return null;
            }
            if (prev <= 1) {
              if (!submittedStartToSocket.current) {
                submittedStartToSocket.current = true;
                if (drawIntervalRef.current) {
                  clearInterval(drawIntervalRef.current);
                  drawIntervalRef.current = null;
                }
                socket.emit("start-stream", { roundSum: roundSumRef.current });
                
                setBalance(prev =>{ 
                  if(prev - roundSumRef.current < 0) {
                    return prev;
                  }
                  return prev - roundSumRef.current;
                });

              }
              return 0;
            }
            return prev - 1;
          });
        },
        1000,
      ],
      guessing: [
        () => {
          setCountdownSeconds((prev) => {
            if (prev == null) {
              return null;
            }
            if (prev > 7) {
              return 7;
            }
            if (prev > 0) {
              return prev - 1;
            }
            if (!submittedGuessToSocket.current) {
              submittedGuessToSocket.current = true;
              checkGuess();
            }
            return 0;
          });
        },
        1000,
      ],
      revealing: [
        () => {
          setTimeLeftPercent((prev) => {
            if (prev <= 0) {
              if (revealingEndHandledRef.current) {
                return 0;
              }
              revealingEndHandledRef.current = true;
              
              stopReloadSound();
              playHundredEndingSofSound();
              stopRevealingSound();
              console.log("REVEALING ENDED playHundredEndingSofSound");
              return 0;
            }else{
              return prev - 1;
            }
          });
        },
        100,
      ],
    };

    if (gameState in STATE_TO_ACTION) {
      STATE_TO_ACTION[gameState]();
    }

    if (!(gameState in STATE_TO_INTERVAL)) {
      return;
    }

    const [intervalFunc, intervalTime] = STATE_TO_INTERVAL[gameState];
    const gameInterval = setInterval(intervalFunc, intervalTime);

    return () => {
      stopRevealingSound();
      pauseTimerSound();
      clearInterval(gameInterval);
    };
  }, [gameState, stoppedAtPiece, totalChunks, isMuted, playUserEnterSound, playTimerSound, playRevealingSound,  playReloadSound, pauseTimerSound, stopReloadSound,  stopRevealingSound, revealingSoundInstance, clearCanvas, checkGuess, socket]);

  // Sync guess input with ref
  useEffect(() => {
    guessInputRef.current = guessInput;
  }, [guessInput]);

  // Optimized pieces left percent calculation
  useEffect(() => {
    const newPercent = Math.max(parseInt(100 * (totalChunks - receivedPieces) / totalChunks), 0);
    piecesLeftPercentRef.current = newPercent;
    setPiecesLeftPercent(newPercent);
  }, [receivedPieces, totalChunks]);

  const applyHundredTokensTopupCelebration = useCallback(() => {
    if (pendingHundredTokensTopupRef.current <= 0) {
      return;
    }

    const topup = pendingHundredTokensTopupRef.current;
    pendingHundredTokensTopupRef.current = 0;
    setBalance((prev) => prev + topup);
    setShowHundredTokensBalanceCelebration(true);
    if (!isMuted) {
      playWinMoneySound();
    }
    if (hundredTokensCelebrationTimeoutRef.current) {
      clearTimeout(hundredTokensCelebrationTimeoutRef.current);
    }
    hundredTokensCelebrationTimeoutRef.current = setTimeout(() => {
      setShowHundredTokensBalanceCelebration(false);
      hundredTokensCelebrationTimeoutRef.current = null;
    }, 2200);
  }, [isMuted, playWinMoneySound]);

  const closeHundredTokensRewardModal = useCallback(() => {
    applyHundredTokensTopupCelebration();
    setShowHundredTokensRewardModal(false);
  }, [applyHundredTokensTopupCelebration]);

  const closeFirstWinCashOutModal = useCallback(() => {
    setShowFirstWinCashOutModal(false);
    if (gameStateRef.current !== 'paused') {
      return;
    }
    socket.emit("user-resume", { time: Date.now() });
    playBtnPlaySound();
    setCountdownSeconds(null);
    setGameState("countdown");
  }, [socket, playBtnPlaySound]);

  useEffect(() => {
    return () => {
      if (hundredTokensCelebrationTimeoutRef.current) {
        clearTimeout(hundredTokensCelebrationTimeoutRef.current);
      }
    };
  }, []);

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    balance,
    myWins,
    gameInfo,
    setGameInfo,
    users,
    setUsers,
    imageCanvasRef,
    piecesLeftPercent,
    piecesLeftPercentRef,
    gameState,
    setGameState,
    countdownSeconds,
    setCountdownSeconds,
    togglePlay,
    isVerifyingGuess,
    guessInput,
    setGuessInput,
    guessHint,
    setGuessHint,
    checkGuess,
    roundSumRef,
    roundsBar,
    roundsBarStepRounds: ROUNDS_BAR_STEP_ROUNDS,
    roundsBarStepPercent: ROUNDS_BAR_STEP_PERCENT,
    isMuted,
    setIsMuted,
    roundsStop,
    isCorrectAnswer,
    lastWinAmount,
    timeLeftPercent,
    hasEnteredRound,
    nextRoundTime,
    nextRoundImageFieldName,
    timeUntilNextRound,
    showLowBalanceModal,
    setShowLowBalanceModal,
    showFreePlayLimitReachedModal,
    setShowFreePlayLimitReachedModal,
    showHundredTokensRewardModal,
    closeHundredTokensRewardModal,
    showHundredTokensBalanceCelebration,
    showFirstWinCashOutModal,
    closeFirstWinCashOutModal,
    requiredAmount,
    setRequiredAmount,
    gameID,
    showLastRoundLossModal,
    setShowLastRoundLoss,
    lastRoundLoss,
    fetchLastRoundLoss
  }), [
    balance, myWins, gameInfo, users, piecesLeftPercent, gameState, countdownSeconds,
    togglePlay, isVerifyingGuess, guessInput, guessHint, checkGuess, roundsBar, isMuted, setIsMuted,
    roundsStop, isCorrectAnswer, lastWinAmount, timeLeftPercent, hasEnteredRound, nextRoundTime,
    nextRoundImageFieldName, timeUntilNextRound, showLowBalanceModal, showFreePlayLimitReachedModal, showHundredTokensRewardModal, showHundredTokensBalanceCelebration, closeHundredTokensRewardModal, showFirstWinCashOutModal, closeFirstWinCashOutModal, requiredAmount, gameID,
    showLastRoundLossModal, lastRoundLoss, fetchLastRoundLoss
  ]);

  return (
    <GameContext.Provider value={contextValue}>
      {children}
    </GameContext.Provider>
  );
};

